// vpn-web-bo/src/pages/accounting/index.tsx
//
// VPN-74 帳務總覽頁：搜尋 + 統計卡 + 裝置帳務表格 + 列點擊→流量明細 + CSV 匯出。
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Input,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
  theme,
} from 'antd';
import type { TableProps } from 'antd';
import {
  DownloadOutlined,
  ReloadOutlined,
  GiftOutlined,
  CloudDownloadOutlined,
  WalletOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  RightOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';
import { authFetch } from '../../utils/api';
import { BrandScopeNotice } from '../../components/BrandScopeNotice';
import { getBrandLabel, tokenManager } from '../../utils/tokenManager';
import { useViewScope } from '../../utils/viewScope';
import {
  formatMb,
  downloadAccountingCsv,
  unwrap,
  describeSourceType,
  type AccountingOverviewRow,
  type AccountingOverviewStats,
  type AccountingOverviewResponse,
  overviewDetailUrl,
} from './lib';

const { Title } = Typography;

const API_PATH = '/api/admin/accounting/overview';

const EMPTY_STATS: AccountingOverviewStats = {
  total_granted_mb: 0,
  total_used_mb: 0,
  total_remaining_mb: 0,
  expiring_soon_count: 0,
  abnormal_device_count: 0,
  quota_fail_open_24h: 0,
};

export const AccountingOverviewPage: React.FC = () => {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const isAllBrand = tokenManager.isAllBrandAdmin();

  const [data, setData] = useState<AccountingOverviewRow[]>([]);
  const [stats, setStats] = useState<AccountingOverviewStats>(EMPTY_STATS);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [keyword, setKeyword] = useState('');
  // 檢視範圍改由全域 Header（useViewScope）驅動；'all' = 聚合、單品牌 = 該品牌。
  const { scope: brandScope } = useViewScope();

  const buildParams = useCallback(() => {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (keyword) params.set('keyword', keyword);
    if (isAllBrand) params.set('brand', brandScope);
    return params;
  }, [page, pageSize, keyword, isAllBrand, brandScope]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_PATH}?${buildParams().toString()}`);
      const result = await res.json();
      const body = unwrap<AccountingOverviewResponse>(result);
      if (body) {
        setData(Array.isArray(body.rows) ? body.rows : []);
        setStats(body.stats ?? EMPTY_STATS);
        setTotal(Number(body.total) || 0);
      } else {
        message.error(result?.msg || '載入帳務總覽失敗');
      }
    } catch {
      message.error('載入帳務總覽失敗');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 檢視範圍變更時回到第一頁（避免停在超出新結果集的頁碼）。
  useEffect(() => {
    setPage(1);
  }, [brandScope]);

  const handleSearch = (value: string) => {
    setPage(1);
    setKeyword(value.trim());
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      // 匯出走全部符合條件的資料（不受分頁限制），故移除 page/pageSize。
      const params = buildParams();
      params.delete('page');
      params.delete('pageSize');
      await downloadAccountingCsv(API_PATH, params, 'accounting-overview');
      message.success('已開始下載 CSV');
    } catch (err) {
      message.error(err instanceof Error ? err.message : '匯出失敗');
    } finally {
      setExporting(false);
    }
  };

  const gotoDetail = useCallback(
    (row: AccountingOverviewRow) => {
      const url = overviewDetailUrl(row);
      if (url) navigate(url);
    },
    [navigate],
  );

  const statCards = useMemo(
    () => [
      {
        key: 'granted',
        title: '已發放總量',
        value: formatMb(stats.total_granted_mb),
        icon: <GiftOutlined style={{ fontSize: 26, color: token.colorPrimary }} />,
      },
      {
        key: 'used',
        title: '已使用總量',
        value: formatMb(stats.total_used_mb),
        icon: (
          <CloudDownloadOutlined
            style={{ fontSize: 26, color: token.colorInfo }}
          />
        ),
      },
      {
        key: 'remaining',
        // fix #4 誠實標示：扣量採 fail-open（查詢/扣帳短暫失敗時不誤斷線），故此數字為
        // 上界、可能高估。過去 24h 有 fail-open 時用警示圖示 + 量化說明不可信區間。
        title: (
          <Space size={4}>
            <span>剩餘有效總量</span>
            <Tooltip
              title={
                stats.quota_fail_open_24h > 0
                  ? `過去 24h 舊版 grant 扣量 fail-open 有 ${stats.quota_fail_open_24h} 筆，舊版剩餘量可能高估。此計數不涵蓋會員 v3，不能用於判斷會員扣帳是否完整。`
                  : '過去 24h 舊版 grant 扣量 fail-open 為 0。此計數不涵蓋會員 v3，不能用於判斷會員扣帳是否完整。'
              }
            >
              {stats.quota_fail_open_24h > 0 ? (
                <WarningOutlined style={{ color: token.colorWarning }} />
              ) : (
                <InfoCircleOutlined style={{ color: token.colorTextTertiary }} />
              )}
            </Tooltip>
          </Space>
        ),
        value: formatMb(stats.total_remaining_mb),
        icon: (
          <WalletOutlined style={{ fontSize: 26, color: token.colorSuccess }} />
        ),
      },
      {
        key: 'expiring',
        title: '即將到期裝置',
        value: stats.expiring_soon_count,
        icon: (
          <ClockCircleOutlined
            style={{ fontSize: 26, color: token.colorWarning }}
          />
        ),
      },
      {
        key: 'abnormal',
        title: '異常裝置',
        value: stats.abnormal_device_count,
        valueStyle:
          stats.abnormal_device_count > 0
            ? { color: token.colorError }
            : undefined,
        icon: (
          <WarningOutlined
            style={{
              fontSize: 26,
              color:
                stats.abnormal_device_count > 0
                  ? token.colorError
                  : token.colorTextTertiary,
            }}
          />
        ),
      },
    ],
    [stats, token],
  );

  const columns: NonNullable<TableProps<AccountingOverviewRow>['columns']> = [
    {
      dataIndex: 'source',
      title: '帳務來源',
      width: 120,
      render: (source: AccountingOverviewRow['source']) => (
        <Tag color={source === 'member_entitlement' ? 'green' : 'default'}>
          {source === 'member_entitlement' ? '會員權益' : '舊版發放'}
        </Tag>
      ),
    },
    {
      dataIndex: 'device_id',
      title: '裝置',
      width: 200,
      render: (id: string) =>
        id ? (
          <Typography.Text
            copyable={{ text: id }}
            ellipsis={{ tooltip: id }}
            style={{ maxWidth: 170, fontSize: 12, fontFamily: 'monospace' }}
          >
            {id}
          </Typography.Text>
        ) : (
          <span style={{ color: '#999' }}>—</span>
        ),
    },
    {
      dataIndex: 'member_account_id',
      title: '會員帳戶 ID',
      width: 135,
      render: (memberAccountId: AccountingOverviewRow['member_account_id']) =>
        memberAccountId != null && memberAccountId !== '' ? (
          <Typography.Text copyable={{ text: String(memberAccountId) }} onClick={event => event.stopPropagation()}>
            {String(memberAccountId)}
          </Typography.Text>
        ) : (
          <span style={{ color: '#999' }}>—</span>
        ),
    },
    {
      dataIndex: 'user_id',
      title: '用戶 ID',
      width: 120,
      render: (uid: AccountingOverviewRow['user_id']) =>
        uid != null && uid !== '' ? String(uid) : <span style={{ color: '#999' }}>—</span>,
    },
    {
      dataIndex: 'brand',
      title: '品牌',
      width: 90,
      render: (brand: string) => <Tag color="blue">{getBrandLabel(brand)}</Tag>,
    },
    {
      dataIndex: 'granted_mb',
      title: '已發放',
      width: 110,
      align: 'right',
      render: (v: number) => formatMb(v),
    },
    {
      dataIndex: 'used_mb',
      title: '已使用',
      width: 110,
      align: 'right',
      render: (v: number) => formatMb(v),
    },
    {
      dataIndex: 'remaining_mb',
      title: '剩餘',
      width: 110,
      align: 'right',
      render: (v: number) => {
        const low = Number(v) <= 0;
        return <span style={low ? { color: token.colorError } : undefined}>{formatMb(v)}</span>;
      },
    },
    {
      dataIndex: 'valid_until',
      title: '有效期至',
      width: 160,
      render: (v: string | null) => {
        if (!v) return <span style={{ color: '#999' }}>—</span>;
        const d = dayjs(v);
        const expired = d.isBefore(dayjs());
        const soon = !expired && d.isBefore(dayjs().add(3, 'day'));
        return (
          <Space size={4}>
            <span>{d.format('YYYY-MM-DD HH:mm')}</span>
            {expired && <Tag color="default" style={{ margin: 0 }}>已到期</Tag>}
            {soon && <Tag color="orange" style={{ margin: 0 }}>即將到期</Tag>}
          </Space>
        );
      },
    },
    {
      dataIndex: 'source_types',
      title: '來源',
      width: 150,
      render: (types: string[]) => {
        if (!Array.isArray(types) || types.length === 0) {
          return <span style={{ color: '#999' }}>—</span>;
        }
        return (
          <Space size={4} wrap>
            {types.map((t) => (
              <Tag key={t} style={{ margin: 0 }}>
                {describeSourceType(t)}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      dataIndex: 'last_used_at',
      title: '最近使用',
      width: 160,
      render: (v: string | null) =>
        v ? dayjs(v).format('YYYY-MM-DD HH:mm') : <span style={{ color: '#999' }}>—</span>,
    },
    {
      title: '操作',
      width: 100,
      fixed: 'right',
      render: (_: unknown, row: AccountingOverviewRow) => (
        <Button
          type="link"
          size="small"
          disabled={row.source === 'member_entitlement' && row.member_account_id == null}
          title={row.source === 'member_entitlement' && row.member_account_id == null
            ? '缺少可信的會員帳戶 ID，無法查看流量明細'
            : row.source === 'member_entitlement'
              ? '查看此會員帳戶在所選日期範圍內的流量（可能包含多部裝置）'
              : '查看舊版裝置流量'}
          onClick={(e) => {
            e.stopPropagation();
            gotoDetail(row);
          }}
        >
          流量明細 <RightOutlined style={{ fontSize: 10 }} />
        </Button>
      ),
    },
  ];

  return (
    <div className="page-enter">
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          gap: 12,
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          帳務總覽
        </Title>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
            重新整理
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExport}
            loading={exporting}
          >
            匯出 CSV
          </Button>
        </Space>
      </div>

      <BrandScopeNotice kind="accounting" style={{ marginBottom: 16 }} />

      {/* 5 統計卡 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {statCards.map((card) => (
          <Col xs={24} sm={12} lg={8} xl={4} key={card.key} style={{ minWidth: 200 }}>
            <Card>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 10,
                    background: token.colorBgTextHover,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {card.icon}
                </div>
                <Statistic
                  title={card.title}
                  value={card.value}
                  valueStyle={{
                    fontSize: 22,
                    fontWeight: 600,
                    ...card.valueStyle,
                  }}
                />
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* 搜尋列 */}
      <div
        className="mobile-filters"
        style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', marginBottom: 12, gap: 8 }}
      >
        <Input.Search
          placeholder="搜尋會員帳戶 ID / 舊用戶 ID / 裝置 / 兌換碼 / channel / campaign"
          allowClear
          enterButton
          onSearch={handleSearch}
          style={{ width: 360, maxWidth: '100%' }}
        />
        <Tooltip title="會員列顯示帳戶層級流量（可能包含多部裝置）；舊版列顯示裝置流量">
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            共 {total} 筆
          </Typography.Text>
        </Tooltip>
      </div>

      <Table
        rowKey={(r) => r.row_key}
        dataSource={data}
        loading={loading}
        columns={columns}
        scroll={{ x: 1240 }}
        onRow={(row) => ({
          onClick: () => gotoDetail(row),
          style: { cursor: row.source === 'member_entitlement' && row.member_account_id == null
            ? 'default' : 'pointer' },
        })}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 筆`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
    </div>
  );
};

export default AccountingOverviewPage;
